import { TellescopeModelFromName, usePermissionQuery } from "@tellescope/tanstack-query";
import { Badge, Button, cn, Popover, PopoverContent, PopoverTrigger } from "@tellescope/ui";
import { Clock, Copy, Ellipsis, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FALLBACK_EVENT_COLOR_HEX } from "../../utils";

export interface TemplateCardProps {
  template: TellescopeModelFromName<"calendar_event_templates">;
  onClick?: (() => void) | { to: string; replace?: boolean };
  onDelete?: () => void;
  onDuplicate?: (() => void) | { to: string; replace?: boolean };
}

export const TemplateCard = ({ template, onClick, onDelete, onDuplicate }: TemplateCardProps) => {
  const { t } = useTranslation("calendar");
  const Component = typeof onClick === "function" ? "button" : typeof onClick === "object" ? Link : "div";

  const {
    delete: { access: deleteAccess },
    create: { access: duplicateAccess },
  } = usePermissionQuery({
    model: "calendar_event_templates",
    actions: ["delete", "create"],
    record: template,
  });
  const canDelete = deleteAccess && !!onDelete;
  const canDuplicate = duplicateAccess && !!onDuplicate;

  return (
    <div
      data-testid={`template-card-${template.id}`}
      className={cn(
        "border rounded-md flex flex-col group relative",
        Component !== "div" && "transition-all hover:shadow-sm"
      )}
    >
      <Component
        className="flex flex-col grow"
        to={typeof onClick === "object" ? onClick.to : ""}
        replace={typeof onClick === "object" ? onClick.replace : undefined}
        onClick={typeof onClick === "function" ? onClick : undefined}
      >
        <div className="p-3 space-y-2 grow">
          <span className="flex items-center gap-2">
            <div
              className={cn("rounded-full h-2.5 w-2.5", !template.color && "border border-gray-300")}
              style={{ backgroundColor: template.color || FALLBACK_EVENT_COLOR_HEX }}
            />
            <p className="text-sm">{template.title}</p>
          </span>
          <div className="text-muted-foreground space-y-2">
            {!!template.description && (
              <span className="flex items-start gap-1 text-xs truncate line-clamp-2">{template.description}</span>
            )}
            <span className="flex items-start gap-1 text-xs truncate line-clamp-2">
              <Clock size={15} />
              {t("{{count}} minutes", { count: template.durationInMinutes })}
            </span>
          </div>
        </div>
        <div className="px-4 bg-muted min-h-9 flex gap-2 items-center rounded-b-md">
          {template.tags?.length ? (
            template.tags?.map((tag) => (
              <Badge
                key={tag}
                variant="outline"
                className="bg-white rounded-full font-light text-muted-foreground h-min"
              >
                {tag}
              </Badge>
            ))
          ) : (
            <p className="text-xs text-muted-foreground font-light italic">{t("No tags")}</p>
          )}
        </div>
      </Component>
      {(canDelete || canDuplicate) && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              data-testid={`template-card-menu-${template.id}`}
              variant="ghost"
              size="sm-icon"
              className="invisible pointer-events-none group-hover:visible data-[state=open]:visible group-hover:pointer-events-auto data-[state=open]:pointer-events-auto absolute top-2 right-2"
            >
              <Ellipsis />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-1 w-min" align="end">
            {canDuplicate &&
              (typeof onDuplicate === "object" ? (
                <Link
                  to={onDuplicate.to}
                  replace={onDuplicate.replace}
                  data-testid={`template-card-duplicate-${template.id}`}
                  className="transition-all font-light flex gap-2 items-center text-sm px-2 py-1 mb-1 hover:bg-gray-100 rounded-sm"
                >
                  <Copy size={12} />
                  {t("Duplicate")}
                </Link>
              ) : (
                <Button
                  onClick={onDuplicate}
                  data-testid={`template-card-duplicate-${template.id}`}
                  variant="ghost"
                  className="font-light"
                >
                  <Copy />
                  {t("Duplicate")}
                </Button>
              ))}
            {canDuplicate && canDelete && <div className="w-full bg-gray-200 h-[1px]" />}
            {canDelete && (
              <Button
                data-testid={`template-card-delete-${template.id}`}
                variant="ghost"
                className="text-destructive"
                onClick={onDelete}
              >
                <Trash2 />
                {t("Delete")}
              </Button>
            )}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
